import { createTheme, ThemeOptions, useTheme } from "@mui/material/styles";
import Tooltip, { TooltipProps } from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import React from "react";

const lightThemeOptions: ThemeOptions = {
  palette: {
    mode: "light",
    primary: {
      main: "#1967d2",
    },
    background: {
      default: "#ffffff",
      paper: "#ffffff",
    },
    text: {
      primary: "#202124",
      secondary: "#5f6368",
    },
  },
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "var(--bg-main)",
        },
      },
    },
  },
};

const darkThemeOptions: ThemeOptions = {
  palette: {
    mode: "dark",
    primary: {
      main: "#8ab4f8",
    },
    background: {
      default: "#121212",
      paper: "#1f1f1f",
    },
    text: {
      primary: "#e8eaed",
      secondary: "#9aa0a6",
    },
  },
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "var(--bg-main)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
  },
};

export const lightTheme = createTheme(lightThemeOptions);
export const darkTheme = createTheme(darkThemeOptions);

export type ThemeMode = "light" | "dark" | "system";

export function getSystemTheme(): "light" | "dark" {
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return "light";
}

export function getEffectiveTheme(mode: ThemeMode): "light" | "dark" {
  if (mode === "system") {
    return getSystemTheme();
  }
  return mode;
}

// Returns the visual styling for rich tooltip content (background, border, shadow)
// Use this to maintain consistent tooltip appearance across the app
export function getRichTooltipSx(isDark: boolean) {
  return {
    borderRadius: "12px",
    backgroundColor: isDark ? "#1f1f1f" : "#ffffff",
    color: isDark ? "#e8eaed" : "#202124",
    border: isDark
      ? "1px solid rgba(255, 255, 255, 0.1)"
      : "1px solid rgba(0, 0, 0, 0.1)",
    boxShadow: isDark
      ? "0 4px 20px rgba(0, 0, 0, 0.5)"
      : "0 4px 20px rgba(0, 0, 0, 0.1)",
  };
}

// Styled tooltip content wrapper for rich content (e.g., previews)
export const StyledTooltipContent: React.FC<{
  children: React.ReactNode;
  isDark: boolean;
}> = ({ children, isDark }) => {
  return (
    <Box
      sx={{
        p: 2,
        ...getRichTooltipSx(isDark),
      }}
    >
      {children}
    </Box>
  );
};

// Extended tooltip props with richContent flag
type StyledTooltipProps = TooltipProps & {
  richContent?: boolean;
};

// Reusable styled tooltip - matches MUI default but with our color scheme
export const StyledTooltip = React.forwardRef<
  HTMLDivElement,
  StyledTooltipProps
>((props, ref) => {
  const {
    slotProps,
    arrow,
    richContent = false,
    children,
    ...otherProps
  } = props;

  const useArrow = arrow !== undefined ? arrow : !richContent;

  const tooltipSx = richContent
    ? {
        // Transparent wrapper for rich content
        backgroundColor: "transparent",
        padding: 0,
        maxWidth: "500px",
        boxShadow: "none",
        border: "none",
      }
    : (theme: any) => ({
        // Styled compact tooltip for simple content
        backgroundColor: theme.palette.mode === "dark" ? "#1f1f1f" : "#ffffff",
        color: theme.palette.mode === "dark" ? "#e8eaed" : "#202124",
        border:
          theme.palette.mode === "dark"
            ? "1px solid rgba(255, 255, 255, 0.1)"
            : "1px solid rgba(0, 0, 0, 0.1)",
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 2px 8px rgba(0, 0, 0, 0.4)"
            : "0 2px 8px rgba(0, 0, 0, 0.15)",
        fontSize: "0.75rem",
        padding: "4px 8px",
        borderRadius: "4px",
        maxWidth: "300px",
      });

  const arrowSx = !richContent
    ? (theme: any) => ({
        color: theme.palette.mode === "dark" ? "#1f1f1f" : "#ffffff",
        "&::before": {
          border:
            theme.palette.mode === "dark"
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
        },
      })
    : undefined;

  return (
    <Tooltip
      ref={ref}
      arrow={useArrow}
      {...otherProps}
      TransitionProps={{
        timeout: 0,
      }}
      slotProps={{
        ...slotProps,
        tooltip: {
          ...slotProps?.tooltip,
          sx: tooltipSx,
        },
        popper: {
          ...slotProps?.popper,
          popperOptions: {
            strategy: "fixed",
            ...slotProps?.popper?.popperOptions,
          },
        },
        arrow: arrowSx
          ? {
              ...slotProps?.arrow,
              sx: arrowSx,
            }
          : slotProps?.arrow,
      }}
    >
      {children}
    </Tooltip>
  );
});
